'use client';

import { Button } from '@repo/ui/components/ui/button';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { Step } from '../../../shared/types';

interface DetailsArbreNavigationProps {
  currentStep: number;
  totalSteps: number;
  onPrevious: () => void;
  onNext: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  steps?: Step[];
  completedSteps?: Set<number>;
  onStepClick?: (step: number) => void;
}

export function DetailsArbreNavigation({
  currentStep,
  totalSteps,
  onPrevious,
  onNext,
  isFirstStep,
  isLastStep,
  steps,
  completedSteps,
  onStepClick,
}: DetailsArbreNavigationProps) {
  return (
    <nav
      className="sticky bottom-0 z-40 border-t border-border/60 bg-background/98 backdrop-blur-md supports-[backdrop-filter]:bg-background/90 shadow-lg ring-1 ring-border/20 dark:border-border/80 dark:bg-background/98 dark:ring-border/30"
      aria-label="Navigation entre les étapes"
    >
      {steps && steps.length > 0 && onStepClick && (
        <div className="container mx-auto px-4 pt-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-thin" role="tablist" aria-label="Raccourcis des étapes">
            {steps.map((step, index) => {
              if (step.id === 'historique') return null;
              const isCurrent = index === currentStep;
              const isCompleted = completedSteps?.has(index) ?? false;
              return (
                <button
                  key={step.id}
                  type="button"
                  role="tab"
                  aria-selected={isCurrent}
                  aria-label={`Étape ${index + 1}: ${step.title}`}
                  onClick={() => onStepClick(index)}
                  className={`group flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-all duration-200 ${
                    isCurrent
                      ? 'border-primary/50 bg-primary/10 text-primary ring-1 ring-primary/20 dark:border-primary/50 dark:bg-primary/20'
                      : isCompleted
                        ? 'border-border/50 bg-primary/5 text-muted-foreground hover:border-primary/30 hover:bg-primary/10 hover:text-primary dark:border-border dark:bg-primary/10'
                        : 'border-border/50 bg-card text-muted-foreground hover:border-border hover:bg-accent/50 dark:border-border dark:hover:bg-accent'
                  }`}
                >
                  {isCompleted && !isCurrent ? (
                    <CheckCircle className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />
                  ) : (
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded text-[10px] font-bold ${
                        isCurrent ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {index + 1}
                    </span>
                  )}
                  <span className="whitespace-nowrap">{step.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="container mx-auto flex items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isFirstStep}
          className="h-10 min-w-[120px] border-border/60 bg-background px-4 text-sm font-semibold shadow-sm transition-all duration-200 hover:border-border hover:bg-accent/80 hover:shadow-md disabled:opacity-50 dark:border-border/80 dark:hover:bg-accent/60 sm:min-w-[140px]"
          size="lg"
          aria-label="Étape précédente"
        >
          <ArrowLeft className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="hidden sm:inline">Étape précédente</span>
          <span className="sm:hidden">Précédent</span>
        </Button>

        <div
          className="flex items-center gap-2 rounded-lg bg-muted/50 px-4 py-2 text-sm font-semibold text-muted-foreground/90 ring-1 ring-border/20 dark:bg-muted/30 dark:text-muted-foreground/80 dark:ring-border/30"
          aria-label="Progression"
        >
          <span>
            Étape {currentStep + 1} sur {totalSteps}
          </span>
        </div>

        {isLastStep ? (
          <Button
            variant="outline"
            size="lg"
            disabled
            className="h-10 min-w-[120px] border-border/60 bg-muted/50 px-4 text-sm font-semibold opacity-60 dark:bg-muted/30 sm:min-w-[140px]"
          >
            <span>✓ Inventaire terminé</span>
          </Button>
        ) : (
          <Button
            onClick={onNext}
            className="h-10 min-w-[120px] bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-md transition-all duration-200 hover:bg-primary hover:shadow-lg dark:bg-primary dark:hover:bg-primary sm:min-w-[140px]"
            size="lg"
            aria-label="Étape suivante"
          >
            <span className="hidden sm:inline">Étape suivante</span>
            <span className="sm:hidden">Suivant</span>
            <ArrowRight className="ml-2 h-4 w-4 shrink-0" aria-hidden="true" />
          </Button>
        )}
      </div>
    </nav>
  );
}

